import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Typography,
  Alert,
  Stack,
  Divider,
  Chip,
} from "@mui/material";
import {
  CheckCircle,
  Cancel,
  Person,
  School,
  CalendarToday,
  Business,
  VerifiedUser,
} from "@mui/icons-material";
import axios from "axios";
import { BASE_URL, VERIFY_CERTIFICATE } from "constants/api";

interface CertificateVerificationData {
  isValid: boolean;
  enrollmentId: string;
  employeeName: string;
  employeeId: string;
  workshopTopic: string;
  workshopTopicAr: string;
  vendorName: string;
  vendorLogo: string | null;
  companyName: string;
  fromDate: string;
  toDate: string;
}

const CertificateVerification: React.FC = () => {
  const { enrollmentId } = useParams<{ enrollmentId: string }>();
  const [loading, setLoading] = useState(true);
  const [verificationData, setVerificationData] = useState<CertificateVerificationData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const verifyCertificate = async () => {
      if (!enrollmentId) {
        setError("Invalid certificate ID");
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get<CertificateVerificationData>(
          `${BASE_URL}${VERIFY_CERTIFICATE(enrollmentId)}`
        );
        setVerificationData(response.data);
      } catch (err: any) {
        if (err.response?.data?.message) {
          setError(err.response.data.message);
        } else {
          setError("Failed to verify certificate. Please check the certificate ID and try again.");
        }
      } finally {
        setLoading(false);
      }
    };

    verifyCertificate();
  }, [enrollmentId]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Verifying Certificate...
          </Typography>
        </Stack>
      </Container>
    );
  }

  if (error || !verificationData) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Card>
          <CardContent>
            <Stack spacing={3} alignItems="center">
              <Cancel sx={{ fontSize: 80, color: "error.main" }} />
              <Typography variant="h4" align="center" color="error">
                Certificate Verification Failed
              </Typography>
              <Alert severity="error" sx={{ width: "100%" }}>
                {error || "Certificate not found or invalid."}
              </Alert>
              <Typography variant="body2" color="text.secondary" align="center">
                If you believe this is an error, please contact the certificate issuer.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Card elevation={3}>
        <CardContent sx={{ p: 4, position: "relative" }}>
          {/* Vendor Logo - Top Left */}
          {verificationData.vendorLogo && (
            <Box
              component="img"
              src={verificationData.vendorLogo}
              alt="Vendor Logo"
              sx={{
                position: "absolute",
                top: 16,
                left: 16,
                maxHeight: 60,
                maxWidth: 200,
                objectFit: "contain",
                zIndex: 1
              }}
              onError={(e: any) => {
                e.target.style.display = "none";
              }}
            />
          )}

          <Stack spacing={3}>
            {/* Header */}
            <Box sx={{ textAlign: "center", pb: 2 }}>
              {verificationData.isValid ? (
                <>
                  <CheckCircle sx={{ fontSize: 80, color: "success.main", mb: 2 }} />
                  <Typography variant="h4" color="success.main" gutterBottom>
                    Certificate Verified
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    This certificate is authentic and valid
                  </Typography>
                </>
              ) : (
                <>
                  <Cancel sx={{ fontSize: 80, color: "error.main", mb: 2 }} />
                  <Typography variant="h4" color="error" gutterBottom>
                    Certificate Not Valid
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    This certificate could not be verified
                  </Typography>
                </>
              )}
            </Box>

            <Divider />

            {/* Status Chip */}
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Chip
                icon={verificationData.isValid ? <VerifiedUser /> : <Cancel />}
                label={verificationData.isValid ? "Valid Certificate" : "Invalid Certificate"}
                color={verificationData.isValid ? "success" : "error"}
                sx={{ fontSize: "1rem", py: 3, px: 1 }}
              />
            </Box>

            <Divider />

            {/* Certificate Details */}
            <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold" }}>
              Certificate Details
            </Typography>

            <Stack spacing={2}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Person color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Employee Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                    {verificationData.employeeName}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <School color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Workshop Topic
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                    {verificationData.workshopTopic}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Business color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Vendor
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                    {verificationData.vendorName}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Business color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Company
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                    {verificationData.companyName}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <CalendarToday color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Workshop Dates
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                    {verificationData.fromDate} - {verificationData.toDate}
                  </Typography>
                </Box>
              </Box>
            </Stack>

            {/* Footer */}
            <Box sx={{ textAlign: "center", pt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                This verification is provided by American Consulting Experts
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
};

export default CertificateVerification;

